import { authConfigSchema } from '@arquivo-morto/contracts';
import { Button } from '@fluentui/react-components';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  type ApiClient,
  createApiClient,
  publicApi,
} from '#src/api/http-client';
import { DevSignIn } from '#src/auth/DevSignIn';
import {
  type DevUser,
  encodeDevToken,
  readDevUser,
  saveDevUser,
} from '#src/auth/dev-identity';
import { StatusScreen } from '#src/shared/StatusScreen';
import { useTeamsHost } from '#src/teams/TeamsHostProvider';
import type { TeamsUser } from '#src/teams/teams-host';

/**
 * Como a pessoa entrou. Cada forma de login é uma fonte de token; o resto da
 * aplicação só recebe o `api` já autenticado. Em produção só existe o SSO do
 * Teams: o escopo do projeto não prevê acesso fora dele.
 */
interface AuthContextValue {
  readonly api: ApiClient;
  /** `teams`: SSO, assinado pelo Entra ID. `dev`: token de dev, sem assinatura. */
  readonly source: 'teams' | 'dev';
  /** Só existe onde faz sentido trocar de pessoa (modo dev, fora do Teams). */
  readonly signOut?: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function TeamsAuth({
  getAuthToken,
  children,
}: {
  getAuthToken: () => Promise<string>;
  children: ReactNode;
}) {
  const value = useMemo<AuthContextValue>(
    () => ({ api: createApiClient(getAuthToken), source: 'teams' }),
    [getAuthToken],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function DevAuth({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<DevUser | null>(readDevUser);

  const value = useMemo<AuthContextValue | null>(() => {
    if (user === null) {
      return null;
    }
    const token = encodeDevToken(user);
    return {
      api: createApiClient(() => Promise.resolve(token)),
      source: 'dev',
      signOut() {
        saveDevUser(null);
        // Nada do usuário anterior pode aparecer para o próximo.
        queryClient.clear();
        setUser(null);
      },
    };
  }, [user, queryClient]);

  if (value === null) {
    return (
      <DevSignIn
        onSignIn={(signedIn) => {
          saveDevUser(signedIn);
          setUser(signedIn);
        }}
      />
    );
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * No Teams, em modo dev, a identidade vem do contexto do Teams, sem SSO: o F5
 * funciona sem configurar nem consentir nada no Entra ID. O contexto não é
 * autenticado, o que só é aceitável porque o backend em modo dev também não
 * confere a assinatura do token.
 */
function TeamsDevAuth({
  user,
  children,
}: {
  user: TeamsUser;
  children: ReactNode;
}) {
  const value = useMemo<AuthContextValue>(() => {
    const token = encodeDevToken(user);
    return {
      api: createApiClient(() => Promise.resolve(token)),
      source: 'dev',
    };
  }, [user]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * O modo de autenticação do backend decide a fonte do token, dentro e fora do
 * Teams:
 *
 * | Modo  | No Teams                    | No navegador                 |
 * | ----- | --------------------------- | ---------------------------- |
 * | entra | SSO do Teams                | Aviso para abrir pelo Teams  |
 * | dev   | Identidade do Teams, sem SSO | Formulário com o e-mail      |
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const host = useTeamsHost();
  const config = useQuery({
    queryKey: ['auth-config'],
    queryFn: () => publicApi.get('/auth/config', authConfigSchema),
    staleTime: Number.POSITIVE_INFINITY,
  });

  if (config.isPending) {
    return <StatusScreen fullPage loading title="Carregando..." />;
  }
  if (config.isError) {
    return (
      <StatusScreen
        fullPage
        tone="error"
        title="Não foi possível falar com o servidor"
        description={config.error.message}
        action={
          <Button onClick={() => config.refetch()}>Tentar de novo</Button>
        }
      />
    );
  }
  if (config.data.mode === 'dev') {
    return host?.user ? (
      <TeamsDevAuth user={host.user}>{children}</TeamsDevAuth>
    ) : (
      <DevAuth>{children}</DevAuth>
    );
  }
  if (host !== null) {
    return <TeamsAuth getAuthToken={host.getAuthToken}>{children}</TeamsAuth>;
  }
  return (
    <StatusScreen
      fullPage
      title="Abra pelo Microsoft Teams"
      description="Esta aplicação usa a sua conta do Teams para entrar. Procure-a na barra lateral do Teams."
    />
  );
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (value === null) {
    throw new Error('useAuth fora do AuthProvider');
  }
  return value;
}

/** Cliente da API com o token de quem entrou. */
export const useApi = (): ApiClient => useAuth().api;
